import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { DocumentUploadZone } from '../components/tracking/DocumentUploadZone';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

type OrderStatus = 'PENDING' | 'IN_PROGRESS' | 'READY_FOR_PICKUP' | 'COMPLETED' | 'CANCELLED';

interface TrackingMedication {
  brandName: string;
  activePrinciple: string;
  form: string;
  dosage: string;
  quantity: number;
  instructions?: string;
  price: string;
  insuranceCoverageAmount?: string;
  finalPrice?: string;
}

interface TrackingPharmacyChain {
  name: string;
  logoUrl?: string;
}

interface TrackingStatusLog {
  status: OrderStatus;
  timestamp: string;
}

interface PublicTracking {
  trackingUUID: string;
  status: OrderStatus;
  medications: TrackingMedication[];
  pharmacyChain: TrackingPharmacyChain;
  statusTimeline: TrackingStatusLog[];
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  notificationsEnabled: boolean;
  insuranceIdImageUrl?: string;
  nationalIdImageUrl?: string;
}

const statusTranslationKeys: Record<string, string> = {
  PENDING: 'status.pending',
  IN_PROGRESS: 'status.inProgress',
  READY_FOR_PICKUP: 'status.readyForPickup',
  COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled',
};

const TrackingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { trackingUUID } = useParams<{ trackingUUID: string }>();
  const queryClient = useQueryClient();

  const { data: tracking, isLoading, error } = useQuery({
    queryKey: ['public-tracking', trackingUUID],
    queryFn: async () => {
      const { data } = await axios.get<PublicTracking>(
        `${API_URL}/orders/track/${trackingUUID}`
      );
      return data;
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
    retry: false,
  });

  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${API_URL}/orders/track/${trackingUUID}/unsubscribe`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-tracking', trackingUUID] });
    },
  });

  const resubscribeMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${API_URL}/orders/track/${trackingUUID}/resubscribe`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-tracking', trackingUUID] });
    },
  });

  // Auto-refresh page meta tags
  useEffect(() => {
    document.title = tracking ? `${t('tracking.title')} - ${tracking.pharmacyChain.name}` : t('tracking.title');
  }, [tracking, t]);

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'PENDING':
        return '#9e9e9e'; // gray
      case 'IN_PROGRESS':
        return '#2196f3'; // blue
      case 'READY_FOR_PICKUP':
        return '#4caf50'; // green
      case 'COMPLETED':
        return '#1b5e20'; // dark green
      case 'CANCELLED':
        return '#f44336'; // red
      default:
        return '#9e9e9e';
    }
  };

  const getStatusLabel = (status: OrderStatus) => {
    return t(statusTranslationKeys[status] ?? 'status.pending');
  };

  const getStatusSteps = () => {
    return ['PENDING', 'IN_PROGRESS', 'READY_FOR_PICKUP', 'COMPLETED'];
  };

  const getCurrentStepIndex = (currentStatus: OrderStatus) => {
    const steps = getStatusSteps();
    const index = steps.indexOf(currentStatus);
    return index >= 0 ? index : 0;
  };

  if (error) {
    const errorMessage = (error as any).response?.data?.message || (error as any).message;
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: '#f5f5f5', position: 'relative' }}>
        {/* Language Switcher */}
        <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }}>
          <LanguageSwitcher />
        </Box>
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h5" gutterBottom color="error">
              {t('errors.trackingNotFound')}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {errorMessage || t('errors.checkLinkTryAgain')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!tracking) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pb: 4 }}>
      {/* Language Switcher */}
      <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }}>
        <LanguageSwitcher />
      </Box>

      {/* Status Banner */}
      <Box
        sx={{
          bgcolor: getStatusColor(tracking.status),
          color: 'white',
          py: 4,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {getStatusLabel(tracking.status)}
        </Typography>
        {tracking.status === 'READY_FOR_PICKUP' && (
          <Alert severity="success" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              {t('tracking.prescriptionReady')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {t('tracking.pickUpAt')} {tracking.pharmacyChain.name}
            </Typography>
          </Alert>
        )}
        {tracking.status === 'COMPLETED' && (
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t('app.thankYou')}
          </Typography>
        )}
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, mt: 4 }}>
        {/* Status Timeline */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('tracking.orderProgress')}
          </Typography>
          <Stepper activeStep={getCurrentStepIndex(tracking.status)} alternativeLabel sx={{ mt: 3 }}>
            {getStatusSteps().map((step) => {
              const statusLog = tracking.statusTimeline.find((log) => log.status === step);
              return (
                <Step key={step}>
                  <StepLabel>
                    <Box>
                      <Typography variant="body2">{getStatusLabel(step as OrderStatus)}</Typography>
                      {statusLog && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(statusLog.timestamp).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
                        </Typography>
                      )}
                    </Box>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Paper>

        {/* Medications List */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('tracking.yourMedications')}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('tracking.medication')}</TableCell>
                  <TableCell align="center">{t('tracking.qty')}</TableCell>
                  <TableCell>{t('common.instructions')}</TableCell>
                  <TableCell align="right">{t('tracking.unitPrice')}</TableCell>
                  <TableCell align="right">{t('common.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tracking.medications.map((med, index) => {
                  // Parse prices with explicit checks
                  const priceValue = med.price && med.price !== '' ? parseFloat(med.price) : 0;
                  const finalPriceValue = med.finalPrice && med.finalPrice !== '' ? parseFloat(med.finalPrice) : null;
                  const coverageValue = med.insuranceCoverageAmount && med.insuranceCoverageAmount !== '' ? parseFloat(med.insuranceCoverageAmount) : null;

                  // Determine which price to use
                  const unitPrice = finalPriceValue !== null ? finalPriceValue : priceValue;
                  const total = unitPrice * med.quantity;
                  const originalTotal = priceValue * med.quantity;

                  // Check if insurance was applied
                  const hasInsurance = coverageValue !== null && finalPriceValue !== null;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {med.brandName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {med.form} - {med.dosage}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="medium">
                          {med.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {med.instructions || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {hasInsurance ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ textDecoration: 'line-through' }}>
                              ${priceValue.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              ${finalPriceValue!.toFixed(2)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" fontWeight="medium">
                            ${priceValue.toFixed(2)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {hasInsurance ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ textDecoration: 'line-through' }}>
                              ${originalTotal.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              ${total.toFixed(2)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" fontWeight="medium">
                            ${total.toFixed(2)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Price Summary */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ minWidth: 300 }}>
              {(() => {
                // Calculate totals
                let totalOriginal = 0;
                let totalInsuranceCoverage = 0;
                let totalFinal = 0;
                let hasInsurance = false;

                tracking.medications.forEach((med) => {
                  const priceValue = med.price && med.price !== '' ? parseFloat(med.price) : 0;
                  const finalPriceValue = med.finalPrice && med.finalPrice !== '' ? parseFloat(med.finalPrice) : null;
                  const coverageValue = med.insuranceCoverageAmount && med.insuranceCoverageAmount !== '' ? parseFloat(med.insuranceCoverageAmount) : null;

                  const originalTotal = priceValue * med.quantity;
                  totalOriginal += originalTotal;

                  if (coverageValue !== null && finalPriceValue !== null) {
                    hasInsurance = true;
                    totalInsuranceCoverage += coverageValue;
                    totalFinal += finalPriceValue * med.quantity;
                  } else {
                    totalFinal += originalTotal;
                  }
                });

                return (
                  <>
                    {hasInsurance && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" color="text.secondary">
                            {t('tracking.originalTotal')}:
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                            ${totalOriginal.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" color="success.main" fontWeight="medium">
                            {t('tracking.insuranceCoverage')}:
                          </Typography>
                          <Typography variant="body1" color="success.main" fontWeight="medium">
                            -${totalInsuranceCoverage.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }} />
                      </>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" fontWeight="bold">
                        {hasInsurance ? `${t('tracking.finalTotal')}:` : `${t('common.total')}:`}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        ${totalFinal.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Box>
        </Paper>

        {/* Document Upload */}
        {trackingUUID && tracking && (
          <Box sx={{ mt: 4, mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#0D1B35', mb: 2 }}>
              Documentos para la farmacia
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Sube tu cédula y carnet de seguro si necesitas cobertura. Estos archivos se
              guardan en tu perfil para futuros pedidos.
            </Typography>
            <DocumentUploadZone
              trackingUUID={trackingUUID}
              type="CEDULA"
              label="Cédula o pasaporte"
              description="Necesario si tu pedido contiene medicamentos controlados."
              initialUrl={tracking.nationalIdImageUrl}
            />
            <DocumentUploadZone
              trackingUUID={trackingUUID}
              type="INSURANCE_CARD"
              label="Carnet de seguro"
              description="Opcional — la farmacia puede procesar tu cobertura si lo subes."
              initialUrl={tracking.insuranceIdImageUrl}
            />
          </Box>
        )}

        {/* Pharmacy Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('tracking.pharmacyInfo')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h5" gutterBottom>
              {tracking.pharmacyChain.name}
            </Typography>
          </Box>
        </Paper>

        {/* Notification Preferences */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('tracking.notificationPreferences')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            {tracking.notificationsEnabled ? (
              <Alert
                severity="info"
                icon={<NotificationsIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  {t('tracking.subscribedMessage')}
                </Typography>
              </Alert>
            ) : (
              <Alert
                severity="warning"
                icon={<NotificationsOffIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  {t('tracking.unsubscribedMessage')}
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {tracking.notificationsEnabled ? (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<NotificationsOffIcon />}
                  onClick={() => unsubscribeMutation.mutate()}
                  disabled={unsubscribeMutation.isPending}
                >
                  {unsubscribeMutation.isPending ? t('tracking.unsubscribing') : t('tracking.unsubscribe')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<NotificationsIcon />}
                  onClick={() => resubscribeMutation.mutate()}
                  disabled={resubscribeMutation.isPending}
                >
                  {resubscribeMutation.isPending ? t('tracking.resubscribing') : t('tracking.resubscribe')}
                </Button>
              )}
            </Box>

            {(unsubscribeMutation.isSuccess || resubscribeMutation.isSuccess) && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {tracking.notificationsEnabled
                    ? t('tracking.resubscribeSuccess')
                    : t('tracking.unsubscribeSuccess')}
                </Typography>
              </Alert>
            )}
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t('tracking.orderId')}: {tracking.trackingUUID.substring(0, 8)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('tracking.lastUpdated')}: {new Date(tracking.updatedAt).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('tracking.autoRefresh')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TrackingPage;
